/**
 * @fileoverview Project Parts Context
 *
 * Shared context that fetches project parts once and provides them to all child sections.
 * Eliminates duplicate API calls for parts data.
 * Includes caching to prevent re-fetches when navigating between tabs.
 *
 * @module RenovationProject/Contexts
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

interface ProjectPart {
  id: number;
  title: string;
  description?: string;
  status: string;
  order?: number;
  label?: {
    id: number;
    name: string;
  };
}

interface ProjectPartsContextValue {
  parts: ProjectPart[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const ProjectPartsContext = createContext<ProjectPartsContextValue | undefined>(undefined);

export const useProjectParts = () => {
  const context = useContext(ProjectPartsContext);
  if (!context) {
    throw new Error('useProjectParts must be used within a ProjectPartsProvider');
  }
  return context;
};

interface ProjectPartsProviderProps {
  projectId: number | null;
  children: React.ReactNode;
}

export const ProjectPartsProvider = React.memo<ProjectPartsProviderProps>(({
  projectId,
  children
}) => {
  const { session } = useSession();
  const [parts, setParts] = useState<ProjectPart[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Track last fetched project ID to prevent unnecessary refetches
  const lastFetchedProjectIdRef = useRef<number | null>(null);
  const isFetchingRef = useRef(false);

  const fetchParts = useCallback(async (force: boolean = false) => {
    if (!projectId || !session) {
      setIsLoading(false);
      return;
    }

    // Skip if already fetched this project (unless forced)
    if (!force && lastFetchedProjectIdRef.current === projectId) {
      setIsLoading(false);
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/${projectId}/parts`,
        { method: 'GET' },
        session
      );

      if (response.ok) {
        const data = await response.json();
        // Sort by order if available, otherwise by id
        const sortedParts = (data || []).sort((a: ProjectPart, b: ProjectPart) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return a.id - b.id;
        });
        setParts(sortedParts);
        lastFetchedProjectIdRef.current = projectId;
      } else {
        throw new Error(`Failed to fetch project parts: ${response.statusText}`);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error fetching project parts');
      console.error('Error fetching project parts:', error);
      setError(error);
      setParts([]);
      lastFetchedProjectIdRef.current = null;
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [projectId, session]);

  useEffect(() => {
    fetchParts();
  }, [projectId, session]);  // Only depend on projectId and session changes

  const value: ProjectPartsContextValue = {
    parts,
    isLoading,
    error,
    refetch: () => fetchParts(true)  // Force refetch
  };

  return (
    <ProjectPartsContext.Provider value={value}>
      {children}
    </ProjectPartsContext.Provider>
  );
});

ProjectPartsProvider.displayName = "ProjectPartsProvider";
